import { API, ApiResponse, handleApiResponse, handleApiError } from './config';

/**
 * Interfaz para los métodos/operadores
 */
export interface Method {
  id: string;
  name: string;
  logo: string;
}

/**
 * Respuesta de datos generales de operaciones
 */
export interface OperationsData {
  rechargeMethods: Method[];
  packetMethods: Method[];
  invoiceMethods: Method[];
  insuranceMethods: Method[];
}

/**
 * Paquete disponible
 */
export interface Packet {
  id: string;
  nombre: string;
  precio: string;
  descripcion?: string;
}

/**
 * Factura disponible (campos devueltos por /fetch-invoices)
 */
export interface Invoice {
  id: string;
  /** Nombre del cliente (puede venir como 'name' o 'client_name') */
  client_name: string;
  name?: string;
  /** Dirección del cliente */
  address?: string;
  /** Estado del servicio: 'L'=Activo, 'S'=Suspendido, 'C'=Cortado, 'V'=Vencido */
  state?: string;
  /** Cantidad de facturas pendientes */
  number_invoices?: number;
  /** Monto base total */
  total: number;
  /** Total + cargo por servicio */
  new_total: number;
  /** Cargo por servicio (pago total) */
  service_charge: number;
  /** Monto base mínimo */
  amount_min?: number;
  /** Mínimo + cargo mínimo */
  new_min_total?: number;
  /** Cargo por servicio (pago mínimo) */
  min_service_charge?: number;
  /** Monto de reconexión */
  amount_recconection?: number;
  /** Monto de acuerdo de pago */
  agreement_amount?: number;
  /** Fecha de vencimiento */
  dueDate?: string;
}

/**
 * Plan de seguro (devuelto por /fetch-insurances)
 */
export interface InsurancePlan {
  id: string;
  name: string;
  description?: string;
  price: number;
  coverage?: string;
  vigency?: string;   // vigencia del plan (puede venir del API externo)
  vehicle_type?: string;
}

/**
 * Transacción reciente
 */
export interface RecentTransaction {
  id: string;
  xid: string;
  phone_number: string;
  amount: number;
  operator: string;
  status: number;
  type: number;
  created_at: string;
}

/**
 * Servicio para las operaciones (recargas, paquetes, facturas, seguros)
 */
export const operationsService = {
  /**
   * Obtener datos generales de operaciones
   */
  getOperationsData: async (): Promise<ApiResponse<OperationsData>> => {
    try {
      const response = await API.post('/operations-data', {});
      return handleApiResponse(response);
    } catch (error) {
      throw handleApiError(error);
    }
  },

  /**
   * Enviar una recarga
   */
  recharge: async (
    phoneNumber: string,
    amount: number,
    methodSelectedId: string
  ): Promise<ApiResponse<{ url: string; transactionId: string }>> => {
    try {
      const response = await API.post('/send-recharge', {
        phoneNumber,
        amount,
        methodSelectedId
      });
      return handleApiResponse(response);
    } catch (error) {
      throw handleApiError(error);
    }
  },

  /**
   * Obtener los movimientos recientes
   */
  getLastMovements: async (): Promise<ApiResponse<RecentTransaction[]>> => {
    try {
      const response = await API.post('/last-movements', {});
      return handleApiResponse(response);
    } catch (error) {
      throw handleApiError(error);
    }
  },

  /**
   * Obtener operadores por tipo de operación
   */
  getOperatorsByType: async (
    typeOperation: string
  ): Promise<ApiResponse<Method[]>> => {
    try {
      const response = await API.post('/operators-by-type', { typeOperation });
      return handleApiResponse(response);
    } catch (error) {
      throw handleApiError(error);
    }
  },

  /**
   * Obtener paquetes disponibles
   */
  fetchPackets: async (
    number: string,
    methodSelectedId: string
  ): Promise<ApiResponse<Packet[]>> => {
    try {
      const response = await API.post('/fetch-packets', {
        number,
        methodSelectedId
      });
      return handleApiResponse(response);
    } catch (error) {
      throw handleApiError(error);
    }
  },

  /**
   * Enviar un paquete
   */
  sendPacket: async (
    number: string,
    methodSelectedId: string,
    packetSelectedId: string,
    amount: number,
    packetName: string
  ): Promise<ApiResponse<{ url: string }>> => {
    try {
      const response = await API.post('/send-packet', {
        number,
        methodSelectedId,
        packetSelectedId,
        amount,
        packetName
      });
      return handleApiResponse(response);
    } catch (error) {
      throw handleApiError(error);
    }
  },

  /**
   * Obtener facturas disponibles
   */
  fetchInvoices: async (
    number: string,
    methodSelectedId: string
  ): Promise<ApiResponse<Invoice[]>> => {
    try {
      const response = await API.post('/fetch-invoices', {
        number,
        methodSelectedId
      });
      return handleApiResponse(response);
    } catch (error) {
      throw handleApiError(error);
    }
  },

  /**
   * Pagar una factura
   */
  sendInvoice: async (
    amount: number,
    number: string,
    invoiceName: string,
    methodSelectedId: string,
    sVar: string
  ): Promise<ApiResponse<{ url: string }>> => {
    try {
      const response = await API.post('/send-invoice', {
        amount,
        number,
        invoiceName,
        methodSelectedId,
        sVar
      });
      return handleApiResponse(response);
    } catch (error) {
      throw handleApiError(error);
    }
  },

  /**
   * Obtener planes de seguros
   */
  fetchInsurances: async (
    methodSelectedId: string
  ): Promise<ApiResponse<InsurancePlan[]>> => {
    try {
      const response = await API.post('/fetch-insurances', {
        methodSelectedId
      });
      return handleApiResponse(response);
    } catch (error) {
      throw handleApiError(error);
    }
  },

  /**
   * Contratar un seguro
   */
  sendInsurance: async (
    amount: number,
    number: string,
    methodSelectedId: string,
    description: string,
    vigency: string,
    vehicleType: string,
    document: string,
    chasis: string
  ): Promise<ApiResponse<{ url: string }>> => {
    try {
      const response = await API.post('/send-insurance', {
        amount,
        number,
        methodSelectedId,
        description,
        vigency,
        vehicleType,
        document,
        chasis
      });
      return handleApiResponse(response);
    } catch (error) {
      throw handleApiError(error);
    }
  },

  /**
   * Revertir una transacción
   * Solo disponible durante 10 minutos después de realizar la transacción
   */
  reverseRecharge: async (
    transactionId: string
  ): Promise<ApiResponse<{ success: boolean }>> => {
    try {
      console.log('Enviando solicitud de reversión a API, ID:', transactionId);
      const response = await API.post('/reverse-transaction', {
        id: transactionId
      });
      console.log('Respuesta de API para reversión:', response);
      return handleApiResponse(response);
    } catch (error) {
      console.error('Error al revertir transacción:', error);
      throw handleApiError(error);
    }
  }
};
